import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChefHat, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-xl bg-orange-600 grid place-items-center">
            <ChefHat className="h-6 w-6 text-white" />
          </div>
          <span className="font-sub font-bold text-2xl">QRFoody</span>
        </Link>
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <h1 className="font-heading text-3xl font-bold">Welcome back</h1>
          <p className="text-slate-400 text-sm mt-1">Sign in to manage your restaurant.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                placeholder="you@restaurant.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-orange-400 hover:text-orange-300 transition-colors">Forgot password?</Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 transition-colors rounded-xl py-3 font-semibold flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            New here?{" "}
            <Link data-testid="goto-register-link" to="/register" className="text-orange-400 font-semibold hover:underline">
              Create a restaurant
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
